// The full program proposal (web edition). The document — including commercial terms — is served by
// the API only after the Department's access code is entered; nothing confidential ships in this bundle.

import { useEffect, useState, type FormEvent } from 'react';
import { ApiError } from '../lib/api.ts';
import { SiteLayout } from '../ui/site.tsx';

type Block =
  | { p: string }
  | { lead: string }
  | { list: string[] }
  | { steps: { title: string; body: string }[] }
  | { table: { head: string[]; rows: string[][]; foot?: string[]; note?: string } }
  | { kv: [string, string][] }
  | { callout: string; tone?: 'info' | 'decision' | 'note' }
  | { figures: { value: string; label: string }[] };

interface Section { id: string; number: string; title: string; blocks: Block[] }
interface Meta { reference: string; issue: string; supersedes: string; date: string; validity: string; submittedTo: string; submittedBy: string; classification: string }
interface Doc { meta: Meta; sections: Section[] }

const CODE_KEY = 'movox.proposal.code';

async function fetchProposal(code: string): Promise<Doc> {
  const res = await fetch('/api/proposal', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, (data as { error?: string }).error ?? res.statusText);
  return data as Doc;
}

const isNum = (s: string) => /^[\s\d.,%+\-–—()PKR$USD×x/]+$/.test(s) && /\d/.test(s);

function BlockView({ b }: { b: Block }) {
  if ('p' in b) return <p>{b.p}</p>;
  if ('lead' in b) return <p className="plead">{b.lead}</p>;
  if ('list' in b) return <ul>{b.list.map((x) => <li key={x}>{x}</li>)}</ul>;
  if ('steps' in b) return <div className="psteps">{b.steps.map((s) => <div key={s.title} className="pstep"><strong>{s.title}</strong>{s.body}</div>)}</div>;
  if ('figures' in b) return <div className="pfigs">{b.figures.map((f) => <div key={f.label} className="pfig"><div className="v">{f.value}</div><div className="l">{f.label}</div></div>)}</div>;
  if ('kv' in b) return <dl className="pkv">{b.kv.map(([k, v]) => [<dt key={`k${k}`}>{k}</dt>, <dd key={`v${k}`}>{v}</dd>])}</dl>;
  if ('callout' in b) return <div className={`callout ${b.tone ?? ''}`}>{b.callout}</div>;
  const t = b.table;
  return (
    <div>
      <div className="gtable-wrap">
        <table className="gtable">
          <thead><tr>{t.head.map((h, i) => <th key={i} className={i > 0 && t.rows.every((r) => isNum(r[i] ?? '') || !r[i]) ? 'num' : ''}>{h}</th>)}</tr></thead>
          <tbody>{t.rows.map((r, ri) => <tr key={ri}>{r.map((c, i) => <td key={i} className={i > 0 && isNum(c) ? 'num' : ''}>{c}</td>)}</tr>)}</tbody>
          {t.foot && <tfoot><tr>{t.foot.map((c, i) => <td key={i} className={i > 0 && isNum(c) ? 'num' : ''}>{c}</td>)}</tr></tfoot>}
        </table>
      </div>
      {t.note && <p className="gtable-note">{t.note}</p>}
    </div>
  );
}

function Gate({ onOpen }: { onOpen: (d: Doc, code: string) => void }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const doc = await fetchProposal(code.trim());
      onOpen(doc, code.trim());
    } catch (err) {
      setError(err instanceof ApiError && err.status === 403 ? 'Too many attempts. Please wait 15 minutes and try again.' : 'That access code was not recognised.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <form className="gate" onSubmit={submit}>
      <img src="/brand/seal.svg" alt="" />
      <div className="gov-kicker" style={{ justifySelf: 'center' }}>Confidential</div>
      <h2>Program proposal — Issue 3</h2>
      <p className="muted">
        The full proposal, including commercial terms, is prepared solely for the Department of Health, Government of Azad Jammu &amp; Kashmir.
        Enter the access code provided by MOVO-X with the transmittal letter.
      </p>
      <input className="input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="Access code" autoComplete="off" autoFocus aria-label="Access code" />
      {error && <div className="callout" role="alert">{error}</div>}
      <button className="btn btn-green btn-lg" disabled={busy || !code.trim()}>{busy ? 'Opening…' : 'Open the proposal'}</button>
      <p className="muted small">Each access is recorded in the program audit trail. To request a code: info@movo-x.com</p>
    </form>
  );
}

export function ProposalPage() {
  const [doc, setDoc] = useState<Doc | null>(null);
  const [checking, setChecking] = useState(() => !!sessionStorage.getItem(CODE_KEY));

  useEffect(() => {
    const saved = sessionStorage.getItem(CODE_KEY);
    if (!saved) return;
    fetchProposal(saved)
      .then(setDoc)
      .catch(() => sessionStorage.removeItem(CODE_KEY))
      .finally(() => setChecking(false));
  }, []);

  if (!doc) {
    return (
      <SiteLayout>
        <section className="band band-ivory2"><div className="wrap">{checking ? <p className="muted" style={{ textAlign: 'center' }}>Opening…</p> : <Gate onOpen={(d, c) => { sessionStorage.setItem(CODE_KEY, c); setDoc(d); }} />}</div></section>
      </SiteLayout>
    );
  }

  const m = doc.meta;
  return (
    <SiteLayout notice={[m.classification, `${m.issue} · ${m.date} · valid ${m.validity}`, m.supersedes]}>
      <section className="band-dark hero">
        <div className="wrap">
          <div className="gov-kicker">Proposal · {m.issue}</div>
          <h1>AJK Smart Health <span className="gold">Kiosk Program</span></h1>
          <div className="hero-ur" lang="ur" dir="rtl">آزاد کشمیر سمارٹ ہیلتھ کیوسک پروگرام</div>
          <dl className="pkv" style={{ background: '#fff', marginTop: 22, maxWidth: 900 }}>
            <dt>Reference</dt><dd>{m.reference}</dd>
            <dt>Submitted to</dt><dd>{m.submittedTo}</dd>
            <dt>Submitted by</dt><dd>{m.submittedBy}</dd>
            <dt>Date · validity</dt><dd>{m.date} · {m.validity}</dd>
            <dt>Classification</dt><dd>{m.classification}</dd>
          </dl>
          <div className="hero-actions no-print">
            <button className="btn btn-gold btn-lg" onClick={() => window.print()}>Print / save as PDF</button>
            <button className="btn btn-outline-light btn-lg" onClick={() => { sessionStorage.removeItem(CODE_KEY); setDoc(null); }}>Close the proposal</button>
          </div>
        </div>
      </section>
      <section className="band band-ivory2">
        <div className="wrap proposal-layout">
          <nav className="proposal-toc no-print" aria-label="Contents">
            <div className="gov-kicker">Contents</div>
            {doc.sections.map((s) => <a key={s.id} href={`#${s.id}`}><span>{s.number}</span>{s.title}</a>)}
          </nav>
          <div className="proposal-doc">
            {doc.sections.map((s) => (
              <section key={s.id} id={s.id} className="psec">
                <div className="psec-head"><span className="psec-num">{s.number}</span><h2>{s.title}</h2></div>
                {s.blocks.map((b, i) => <BlockView key={i} b={b} />)}
              </section>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
